import { useState } from "react";
import { toast } from "sonner";
import { api, apiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { formatDateID } from "@/lib/constants";
import { Syringe, PencilSimple, FloppyDisk, X } from "@phosphor-icons/react";

const VAKSIN_OPTS = ["HB-0", "BCG", "Polio", "DPT-HB-Hib", "IPV", "PCV", "Rotavirus", "MR", "JE"];
const inputCls = "w-full rounded-lg border border-slate-200 px-2.5 py-1.5 text-sm outline-none focus:border-rose-400 focus:ring-2 focus:ring-rose-100";
const labelCls = "mb-1 block text-xs font-semibold text-slate-500";

export function isImmunReservation(r) {
  const t = `${r?.kategori_layanan || ""} ${r?.jenis_layanan || ""}`.toLowerCase();
  const hasVax = Array.isArray(r?.jenis_vaksin) ? r.jenis_vaksin.length > 0 : !!r?.jenis_vaksin;
  return /imunisasi|vaksin/.test(t) || hasVax;
}

function maskNik(v) {
  const digits = String(v || "").replace(/\D/g, "");
  if (!digits) return "";
  if (digits.length <= 4) return "•".repeat(digits.length);
  return "•".repeat(digits.length - 4) + digits.slice(-4);
}

function Row({ label, value }) {
  const arr = Array.isArray(value) ? value.filter(Boolean) : null;
  const empty = value === null || value === undefined || value === "" || (arr && arr.length === 0);
  return (
    <div className="flex justify-between gap-4 border-b border-rose-50 py-1.5 text-sm last:border-0">
      <span className="shrink-0 text-slate-500">{label}</span>
      <span className={`text-right font-semibold ${empty ? "italic text-amber-500" : "text-slate-800"}`}>
        {empty ? "Belum diisi" : (arr ? arr.join(", ") : value)}
      </span>
    </div>
  );
}

export default function ImunisasiFill({ reservation, onChanged }) {
  const r = reservation;
  const { user } = useAuth();
  const isAdmin = user?.role === "admin" || user?.role === "super_admin";
  const [editing, setEditing] = useState(false);
  const [saving, setSaving] = useState(false);
  const [f, setF] = useState({});

  if (!r || !isImmunReservation(r)) return null;

  const start = () => {
    setF({
      nama_bayi: r.nama_bayi || "",
      tanggal_lahir_bayi: (r.tanggal_lahir_bayi || "").slice(0, 10),
      tempat_lahir: r.tempat_lahir || "",
      jenis_kelamin_bayi: r.jenis_kelamin_bayi || "",
      nik_anak: r.nik_anak || "",
      nik: r.nik || "",
      nama_ibu: r.nama_ibu || "",
      no_whatsapp: r.no_whatsapp || "",
      alamat: r.alamat || "",
      kelurahan: r.kelurahan || "",
      kecamatan: r.kecamatan || "",
      jenis_vaksin: Array.isArray(r.jenis_vaksin) ? r.jenis_vaksin : (r.jenis_vaksin ? [r.jenis_vaksin] : []),
      imunisasi_terakhir: r.imunisasi_terakhir || "",
      tanggal_imunisasi_terakhir: (r.tanggal_imunisasi_terakhir || "").slice(0, 10),
      riwayat_kesehatan: r.riwayat_kesehatan || "",
    });
    setEditing(true);
  };
  const set = (k, v) => setF((p) => ({ ...p, [k]: v }));
  const setDigits = (k, v, max) => setF((p) => ({ ...p, [k]: v.replace(/\D/g, "").slice(0, max) }));
  const toggleVax = (v) => setF((p) => {
    const cur = p.jenis_vaksin || [];
    return { ...p, jenis_vaksin: cur.includes(v) ? cur.filter((x) => x !== v) : [...cur, v] };
  });
  const save = async () => {
    if (f.nik_anak && !/^\d{16}$/.test(f.nik_anak)) { toast.error("NIK Anak harus tepat 16 digit angka"); return; }
    setSaving(true);
    try {
      await api.patch(`/reservations/${r.id}/imunisasi`, f);
      toast.success("Data imunisasi disimpan");
      setEditing(false);
      onChanged && onChanged();
    } catch (e) { toast.error(apiError(e)); } finally { setSaving(false); }
  };

  const prev = Array.isArray(r.imunisasi_terakhir) ? r.imunisasi_terakhir.join(", ") : r.imunisasi_terakhir;
  const ad = r.alamat_data || {};

  return (
    <div className="rounded-2xl border border-rose-100/70 bg-white p-4" data-testid="detail-imunisasi">
      <div className="mb-1 flex items-center justify-between">
        <p className="flex items-center gap-1.5 font-heading text-sm font-extrabold text-[#DB2777]">
          <Syringe size={16} weight="duotone" /> Data Imunisasi
        </p>
        {isAdmin && !editing && (
          <button data-testid="imun-edit" onClick={start}
            className="inline-flex items-center gap-1 rounded-lg bg-rose-50 px-2.5 py-1 text-xs font-bold text-rose-600 hover:bg-rose-100">
            <PencilSimple size={13} weight="bold" /> Lengkapi / Ubah
          </button>
        )}
      </div>

      {!editing ? (
        <div>
          <Row label="Nama Bayi" value={r.nama_bayi} />
          <Row label="NIK Anak" value={r.nik_anak ? maskNik(r.nik_anak) : ""} />
          <Row label="Tanggal Lahir Bayi" value={r.tanggal_lahir_bayi ? formatDateID(r.tanggal_lahir_bayi) : ""} />
          <Row label="Tempat Lahir Bayi" value={r.tempat_lahir} />
          <Row label="Jenis Kelamin" value={r.jenis_kelamin_bayi} />
          <Row label="Nama Orang Tua / Wali" value={r.nama_ibu} />
          <Row label="NIK Orang Tua / Wali" value={r.nik ? maskNik(r.nik) : ""} />
          <Row label="WhatsApp" value={r.no_whatsapp} />
          <Row label="Kelurahan" value={r.kelurahan || ad.kelurahan_nama} />
          <Row label="Kecamatan" value={r.kecamatan || ad.kecamatan_nama} />
          <Row label="Vaksin Sebelumnya" value={prev} />
          <Row label="Vaksin Diminta" value={Array.isArray(r.jenis_vaksin) ? r.jenis_vaksin : (r.jenis_vaksin ? [r.jenis_vaksin] : [])} />
          <Row label="Catatan" value={r.riwayat_kesehatan} />
        </div>
      ) : (
        <div className="space-y-2.5">
          <div className="grid grid-cols-2 gap-2.5">
            <div>
              <label className={labelCls}>Nama Bayi</label>
              <input data-testid="imun-f-nama-bayi" className={inputCls} value={f.nama_bayi} onChange={(e) => set("nama_bayi", e.target.value)} />
            </div>
            <div>
              <label className={labelCls}>Tanggal Lahir Bayi</label>
              <input data-testid="imun-f-dob" type="date" className={inputCls} value={f.tanggal_lahir_bayi} onChange={(e) => set("tanggal_lahir_bayi", e.target.value)} />
            </div>
            <div>
              <label className={labelCls}>Tempat Lahir Bayi</label>
              <input data-testid="imun-f-tempat-lahir" className={inputCls} value={f.tempat_lahir} onChange={(e) => set("tempat_lahir", e.target.value)} />
            </div>
            <div>
              <label className={labelCls}>NIK Anak (16 digit)</label>
              <input data-testid="imun-f-nik-anak" inputMode="numeric" maxLength={16} className={inputCls} value={f.nik_anak} onChange={(e) => setDigits("nik_anak", e.target.value, 16)} placeholder="16 digit NIK anak" />
            </div>
            <div>
              <label className={labelCls}>Jenis Kelamin</label>
              <select data-testid="imun-f-jk" className={inputCls} value={f.jenis_kelamin_bayi} onChange={(e) => set("jenis_kelamin_bayi", e.target.value)}>
                <option value="">-</option>
                <option value="Laki-laki">Laki-laki</option>
                <option value="Perempuan">Perempuan</option>
              </select>
            </div>
            <div>
              <label className={labelCls}>Nama Orang Tua / Wali</label>
              <input data-testid="imun-f-ortu" className={inputCls} value={f.nama_ibu} onChange={(e) => set("nama_ibu", e.target.value)} />
            </div>
            <div>
              <label className={labelCls}>NIK Orang Tua / Wali</label>
              <input data-testid="imun-f-nik-ortu" inputMode="numeric" maxLength={16} className={inputCls} value={f.nik} onChange={(e) => setDigits("nik", e.target.value, 16)} placeholder="16 digit NIK ortu/wali" />
            </div>
            <div>
              <label className={labelCls}>WhatsApp Orang Tua</label>
              <input data-testid="imun-f-wa" className={inputCls} value={f.no_whatsapp} onChange={(e) => set("no_whatsapp", e.target.value)} />
            </div>
            <div>
              <label className={labelCls}>Kelurahan</label>
              <input data-testid="imun-f-kel" className={inputCls} value={f.kelurahan} onChange={(e) => set("kelurahan", e.target.value)} />
            </div>
            <div>
              <label className={labelCls}>Kecamatan</label>
              <input data-testid="imun-f-kec" className={inputCls} value={f.kecamatan} onChange={(e) => set("kecamatan", e.target.value)} />
            </div>
          </div>
          <div>
            <label className={labelCls}>Alamat Lengkap</label>
            <textarea data-testid="imun-f-alamat" rows={2} className={inputCls} value={f.alamat} onChange={(e) => set("alamat", e.target.value)} />
          </div>
          <div>
            <label className={labelCls}>Vaksin Diminta (boleh lebih dari satu)</label>
            <div className="flex flex-wrap gap-1.5" data-testid="imun-f-vaksin">
              {VAKSIN_OPTS.map((v) => {
                const on = (f.jenis_vaksin || []).includes(v);
                return (
                  <button key={v} type="button" data-testid={`imun-f-vaksin-${v}`} onClick={() => toggleVax(v)}
                    className={`rounded-full px-2.5 py-1 text-xs font-semibold transition-colors ${on ? "bg-[#DB2777] text-white" : "bg-slate-100 text-slate-600 hover:bg-slate-200"}`}>
                    {v}
                  </button>
                );
              })}
            </div>
          </div>
          <div className="grid grid-cols-2 gap-2.5">
            <div>
              <label className={labelCls}>Vaksin Sebelumnya (teks)</label>
              <input data-testid="imun-f-prev" className={inputCls} value={f.imunisasi_terakhir} onChange={(e) => set("imunisasi_terakhir", e.target.value)} placeholder="cth. HB-0, BCG" />
            </div>
            <div>
              <label className={labelCls}>Tgl Vaksin Terakhir</label>
              <input data-testid="imun-f-prev-tgl" type="date" className={inputCls} value={f.tanggal_imunisasi_terakhir} onChange={(e) => set("tanggal_imunisasi_terakhir", e.target.value)} />
            </div>
          </div>
          <div>
            <label className={labelCls}>Catatan</label>
            <textarea data-testid="imun-f-catatan" rows={2} className={inputCls} value={f.riwayat_kesehatan} onChange={(e) => set("riwayat_kesehatan", e.target.value)} />
          </div>
          <div className="flex gap-2 pt-1">
            <button data-testid="imun-save" onClick={save} disabled={saving}
              className="inline-flex items-center gap-1 rounded-lg bg-[#DB2777] px-3 py-1.5 text-xs font-bold text-white hover:bg-[#be1e63] disabled:opacity-60">
              <FloppyDisk size={14} weight="bold" /> {saving ? "Menyimpan…" : "Simpan"}
            </button>
            <button data-testid="imun-cancel" onClick={() => setEditing(false)}
              className="inline-flex items-center gap-1 rounded-lg bg-slate-100 px-3 py-1.5 text-xs font-bold text-slate-600 hover:bg-slate-200">
              <X size={14} weight="bold" /> Batal
            </button>
          </div>
        </div>
      )}
    </div>
  );
}
